// Shared date formatting for server-supplied date strings.
//
// Backend serializes Pydantic `date` fields as date-only ISO strings
// ("YYYY-MM-DD"). Native `new Date("YYYY-MM-DD")` parses these as UTC
// midnight, which `.toLocaleDateString()` then shifts into the viewer's
// local zone — causing an off-by-one day in negative-offset timezones.
// Every server date string must flow through `parseServerDate` so the
// rendered calendar day matches the stored calendar day in any zone.
import { format, parseISO } from "date-fns";

export function parseServerDate(value) {
  if (!value) return null;
  const dateOnly = String(value).split("T")[0];
  if (!dateOnly) return null;
  const d = parseISO(dateOnly);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Full timestamps (created_at, timestamp) come back tz-NAIVE from Mongo reads
// (UTC stored, serialized without "Z"), so a bare `new Date(s)` parses them
// as LOCAL time and skews relative displays by the UTC offset ("4 minutes
// ago" renders as "about 6 hours ago" in UTC+5:30). Append "Z" unless an
// explicit offset is already present.
export function parseUtcTimestamp(value) {
  if (!value) return null;
  const s = String(value);
  const d = new Date(/Z$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDateShort(value) {
  const d = parseServerDate(value);
  return d ? format(d, "MMM d, yy") : "—";
}

export function formatDateLong(value) {
  const d = parseServerDate(value);
  return d ? format(d, "EEE, MMM d, yyyy") : "—";
}

export function formatDateFull(value) {
  const d = parseServerDate(value);
  return d ? format(d, "MMM d, yyyy") : "—";
}

// Compact day-month for the mobile collaborator view ("12 Jun") so the four
// date columns fit a phone screen. tz-safe via parseServerDate (see top).
export function formatDateCompact(value) {
  const d = parseServerDate(value);
  return d ? format(d, "d MMM") : "—";
}

// Canonical activity-start for display, filtering, sorting, positioning.
// Priority: actual (work has begun) → early (CPM forecast) → planned (user-authored).
// CPM is the source of truth once it has run; planned_start is the fallback
// when CPM hasn't populated early_start yet (e.g. newly-created activity
// before the first schedule run). See plan calm-imagining-tome.md.
// Drift from this order was the actual root cause of the off-by-one display
// bug — three surfaces reading three different fallbacks for the same activity.
export function selectActivityStart(activity) {
  if (!activity) return null;
  return (
    activity.actual_start ||
    activity.early_start ||
    activity.planned_start ||
    null
  );
}

export function selectActivityFinish(activity) {
  if (!activity) return null;
  return (
    activity.actual_finish ||
    activity.early_finish ||
    activity.planned_finish ||
    null
  );
}
